import React, { PropsWithChildren } from "react";
import styled from "styled-components";
import { TextAvatar } from "./avatar/TextAvatar";
import { Button, ButtonProps } from "./button/Button";
import { colors } from "./colors/color";
import { Flex, FlexItem } from "./compositeComponents/FlexHelpers";
import { PopupMenuButton, PopupMenuButtonProps } from "./compositeComponents/PopupMenuButton";
import { Icon, iconName } from "./icons/Icons";
import { StyledButton } from "./styles/button2Styles";
import { P } from "./typography/Body";
import { H2, H3, H4 } from "./typography/Heading";
import { ToggletipTheOne } from "./ToggletipTheOne";
import { PageSectionVariant } from "./PageSectionDefs";

// -----------------------------------------------------------------
// ---- See notes in PageSectionDefs.ts
// -----------------------------------------------------------------

export type PageSectionHeaderProps = {
    icon?:iconName,
    headerStyleVariant:PageSectionVariant,
    contentStyleVariant:PageSectionVariant,
    headerVariant?:'H2' | 'H3' | 'H4',
    subTitle?:{
        text:string,
        icon?:iconName,
        iconColor?:string,
    },
    avatar?:{
        text:string,
    },
    tooltip?:React.ReactNode,
    tooltipHorizontalAlignment?:'left' | 'right',
    button?:ButtonProps,
    customButtonContent?: React.ReactNode,
    popupMenuButton?:PopupMenuButtonProps,
    defaultCollapsed?:boolean,
    onCollapseChanged?:(collapsed:boolean)=>void,
    padding?:string,
    style?:React.CSSProperties
}

export function PageSectionHeader(props:PropsWithChildren<PageSectionHeaderProps>):JSX.Element
{
    const [collapsed, setCollapsed] = React.useState(
        props.defaultCollapsed===undefined ? false : props.defaultCollapsed
    );

    React.useEffect(()=>{
        if(props.defaultCollapsed !== undefined && props.defaultCollapsed !== collapsed)
            setCollapsed(props.defaultCollapsed);
    },[props.defaultCollapsed]);

    const content = 
        <Flex justifyContent="space-between">
      
            <Flex flexDirection='column' grow='1' gap={2}>
                
                <Flex alignItems='center' justifyContent='space-between' gap={8}>
                    
                    <Flex alignItems='center' gap={8} grow='1'>
                        {props.defaultCollapsed !== undefined &&
                            <Button 
                                className="page-section-header-button"
                                variant='text'
                                icon={collapsed ? 'arrows_chevron_chevronDown' : 'arrows_chevron_chevronUp'}
                                onClick={() => {
                                    setCollapsed(!collapsed);
                                    props.onCollapseChanged?.(!collapsed);
                                }} />
                        }

                        {props.icon && 
                            <FlexItem flexGrow='0' className="page-section-header-main-icon">
                                <Icon name={props.icon} />
                            </FlexItem>
                        }

                        <FlexItem>
                                {(props.headerVariant === undefined || props.headerVariant === 'H2') && <H2>{props.children}</H2>}
                                {props.headerVariant === 'H3' && <H3>{props.children}</H3>}
                                {props.headerVariant === 'H4' && <H4>{props.children}</H4>}
                        </FlexItem>

                        {props.avatar &&
                            <FlexItem flexGrow='0'>
                                <TextAvatar text={props.avatar.text} />
                            </FlexItem>
                        }

                        {props.tooltip && 
                            <ToggletipTheOne horizontalAlignment={props.tooltipHorizontalAlignment}>
                                {props.tooltip}
                            </ToggletipTheOne>
                        }
                        
                    </Flex>
    
                    {props.subTitle &&
                    <Flex gap={4} alignItems='center'>
                        {props.subTitle.icon &&
                            <span className="page-section-header-icon">
                                <Icon
                                    name={props.subTitle.icon}
                                    color={props.subTitle.iconColor}
                                    size="x-small"
                                />
                            </span>
                        }
                        <P>{props.subTitle.text}</P>
                    </Flex>
                }
                    {props.button &&
                        <div style={{flexGrow:0}}>
                            <Button size="32px" {...props.button} />
                        </div>
                    }
                    
                    {props.customButtonContent &&
                        <div style={{flexGrow:0}}>
                            {props.customButtonContent}
                        </div>
                    }
                    {props.popupMenuButton &&
                        <div style={{flexGrow:0}}>
                            <PopupMenuButton {...props.popupMenuButton} />
                        </div>
                    }

                </Flex>


                
            </Flex>

        </Flex>;

    return  <PageSectionHeaderBase 
                headerStyleVariant={props.headerStyleVariant}
                contentStyleVariant={props.contentStyleVariant}
                collapsed={collapsed}
                onCollapseChanged={props.onCollapseChanged}
                padding={props.padding}
                style={props.style}>

                {content}

            </PageSectionHeaderBase>;

}

export type PageSectionHeaderBaseProps = {
    headerStyleVariant:PageSectionVariant,
    contentStyleVariant:PageSectionVariant,
    collapsed:boolean,
    onCollapseChanged?:(collapsed:boolean)=>void,
    padding?:string,
    style?:React.CSSProperties
}

export function PageSectionHeaderBase(props:PropsWithChildren<PageSectionHeaderBaseProps>):JSX.Element
{

    if(props.headerStyleVariant === 'card')
        return  <CardHeader collapsed={props.collapsed} contentStyleVariant={props.contentStyleVariant || 'card'} padding={props.padding || '16px'}>
                    {props.children}
                </CardHeader>

    return <MinimalHeader collapsed={props.collapsed} contentStyleVariant={props.contentStyleVariant || 'minimal'} padding={props.padding || '16px 0px'}>
                {props.children}
            </MinimalHeader>;
}

const HeaderBase = styled.header`
    & ${H2} {
        font-size: 18px;
    }

    & ${H3} {
        font-size: 16px;
    }

    & ${H4} {
        font-size: 14px;
    }

    & ${StyledButton} {
        margin-bottom: -8px;
        margin-top: -8px;
    }   
`;

const MinimalHeader  = styled(HeaderBase)<{collapsed:boolean, contentStyleVariant:PageSectionVariant, padding:string}>`
    background: transparent;
    padding: ${props => props.padding || '16px 0'};
`;

const cardRadius = '6px';

const CardHeader = styled(HeaderBase)<{collapsed:boolean, contentStyleVariant:PageSectionVariant, padding:string}>`
    background: white;
    padding: ${props => props.padding || '16px'};
    border-top: 1px solid ${colors.section.border.normal};
    border-right: 1px solid ${colors.section.border.normal};
    border-left: 1px solid ${colors.section.border.normal};
    border-bottom: 1px solid ${colors.section.border.normal};
    border-top-left-radius: ${cardRadius};
    border-top-right-radius: ${cardRadius};
    border-bottom-left-radius: ${props => props.collapsed || props.contentStyleVariant==='minimal' ? cardRadius : '0'};
    border-bottom-right-radius: ${props => props.collapsed || props.contentStyleVariant==='minimal' ? cardRadius : '0'};
`;